import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { TemplateLocaleService } from "@domain/project/template";
import { User } from "@domain/user/user";
import { UserService } from "@domain/user/user.service";
import { environment } from "@environments/environment";
import { LanguageDto } from "@lib/dtos/language/language.dto";
import { TranslationDto } from "@lib/dtos/language/translation.dto";
import { first, interval, Observable, of, switchMap, tap } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class LanguageService {
  private http = inject(HttpClient);
  private userService = inject(UserService);
  private templateLocaleService = inject(TemplateLocaleService);

  private static readonly UPDATE_INTERVAL_MILLIS: number = 24 * 60 * 60 * 1000; // 24 hours

  constructor() {
    interval(LanguageService.UPDATE_INTERVAL_MILLIS)
      .pipe(switchMap(() => this.updateTranslations()))
      .subscribe();
  }

  updateTranslations(): Observable<void> {
    if (!navigator.onLine) {
      return of(undefined);
    }
    // set high limit to avoid pagination
    const url = `${environment.apiUrl}/language`;
    const languages$ = this.templateLocaleService?.allSupportedLanguageCodes$ ?? of([] as string[]);
    return languages$.pipe(
      first(),
      switchMap((languageCodes) =>
        this.http.get<LanguageDto[]>(url).pipe(
          tap((response: LanguageDto[]) => {
            languageCodes.forEach((localeString) => {
              this.updateLocallyStoredTranslationFile(response, localeString);
            });
          }),
        ),
      ),
      switchMap(() => of(undefined)),
    );
  }

  switchLanguage(newLanguageString: string): Observable<User | void> {
    const languages$ = this.templateLocaleService?.allSupportedLanguageCodes$ ?? of([] as string[]);
    return languages$.pipe(
      first(),
      switchMap((languageCodes) => {
        if (!languageCodes.includes(newLanguageString)) {
          return of(undefined);
        }
        return this.userService.user$.pipe(
          first(),
          switchMap((user) => {
            if (user.language === newLanguageString) {
              return of(undefined);
            }
            return this.userService.updateLanguage(newLanguageString).pipe(tap(() => location.reload()));
          }),
        );
      }),
    );
  }

  private updateLocallyStoredTranslationFile(keys: LanguageDto[], localeString: string) {
    const translations = this.mapTranslations(keys, localeString);
    localStorage.setItem(`${localeString}_translations`, JSON.stringify(translations));
  }

  private mapTranslations(inputArray: LanguageDto[], localeString: string): { [key: string]: string } {
    const result: { [key: string]: string } = {};

    inputArray.forEach((keyObject) => {
      const keyName = keyObject.key_name.web;
      const translationObj = keyObject.translations.find(
        (translation: TranslationDto) =>
          translation.language_iso === localeString || translation.language_iso === localeString.split("-")[0],
      );

      if (translationObj && Number(translationObj.is_untranslated) === 0) {
        result[keyName] = translationObj.translation;
      }
    });

    return result;
  }
}
